// The player page (spec/dashboard.md section 11.2): the header with presence (re-read every 30 s while visible), the action
// bar, the Bindings / Sessions / Collections cards, the typed profile tree over the pinned release, the History drawer
// (bound to the URL: `?history=1&collection=&key=&since=&from=&to=&source=`) and the commands — restore, reset, erase, export — which never stage
// and are refused while the player has staged edits; Ban… / Unban (M7 slice 3) and the Banned card while it has a ban;
// M7 slice 7: the Events drawer (`?events=1`, the player's raw analytics feed) and the Tester switch beside Ban.
import { useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router';
import { ApiError } from '../../api/client';
import type { SaveVersion } from '../../api/types';
import { PlayerTree } from '../../players/PlayerTree';
import { useReleaseInfo } from '../../players/releaseCatalog';
import { usePlayer } from '../../players/usePlayer';
import { useGame } from '../../shell/GameContext';
import { routes } from '../../shell/routes';
import { EmptyState } from '../../ui/Banner';
import { Button } from '../../ui/Button';
import { useUrlParam } from '../../ui/Modal';
import { useToasts } from '../../ui/Toast';
import { useWorkspace, useWorkspaceVersion } from '../../workspace/react';
import { CommandDialogsHost, type CommandName } from './PlayerCommands';
import { banActive, errorText, guardStaged } from './commands';
import { EventsDrawer } from './EventsDrawer';
import { HISTORY_PARAMS, HistoryDrawer, type HistoryFilter } from './HistoryDrawer';
import { TesterSwitch } from './TesterSwitch';
import { PlayerCards, PlayerHeader } from './PlayerCards';
import './players.css';
import { LoadError, LoadingPane } from '../../ui/PageStates';
import { Activity, AlertTriangle, ArrowLeft, Ban, Download, Eraser, History, Lock, Receipt, RotateCcw, Unlock, User } from '../../ui/icons';

export const ONLINE_BANNER = "This player is online: a save will be refused unless forced (the device's next save can overwrite a forced edit).";
export const NOT_FOUND = 'This player was erased or never existed.';

export function PlayerPage() {
  const { env: envSlug = '', player: playerId = '' } = useParams();
  const { org, game, can } = useGame();
  if (!can('players_read', envSlug)) {
    return (
      <section className="env-page players-page" data-testid="player">
        <div className="page-head">
          <h1>Player</h1>
        </div>
        <EmptyState icon={<Lock />} title="You cannot read players here" body={`Reading the players of “${envSlug}” needs the 'players_read' permission on this environment.`} />
      </section>
    );
  }
  return <PlayerView key={`${envSlug}/${playerId}`} envSlug={envSlug} playerId={playerId} org={org} gameSlug={game.slug} />;
}

function PlayerView({ envSlug, playerId, org, gameSlug }: { envSlug: string; playerId: string; org: string; gameSlug: string }) {
  const { can } = useGame();
  const q = usePlayer(envSlug, playerId);
  const release = useReleaseInfo(envSlug);
  const workspace = useWorkspace();
  useWorkspaceVersion(workspace);
  const toasts = useToasts();
  const [params] = useSearchParams();
  const history = useUrlParam('history');
  const events = useUrlParam('events');
  const [command, setCommand] = useState<CommandName | null>(null);
  const [restoreVersion, setRestoreVersion] = useState<SaveVersion | null>(null);
  const [restoreAt, setRestoreAt] = useState<{ collection?: string } | null>(null);
  const canWrite = can('players_write', envSlug);
  const writeMissing = canWrite ? null : 'players_write';
  const back = routes.players(org, gameSlug, envSlug);

  if (q.error) {
    const notFound = q.error instanceof ApiError && q.error.status === 404;
    return (
      <section className="env-page players-page" data-testid="player">
        <div className="page-head">
          <h1>Player</h1>
        </div>
        {notFound ? (
          <EmptyState icon={<User />} title={NOT_FOUND} body={<code>{playerId}</code>} action={<Link to={back}>Back to the players</Link>} />
        ) : (
          <>
            <LoadError>{errorText(q.error)}</LoadError>
            <p>
              <Link to={back}>Back to the players</Link>
            </p>
          </>
        )}
      </section>
    );
  }
  const player = q.data;
  if (!player) {
    return (
      <section className="env-page players-page" data-testid="player">
        <div className="page-head">
          <h1>Player</h1>
        </div>
        <LoadingPane rows={4} label="Loading the player" />
      </section>
    );
  }

  const filter = Object.fromEntries(HISTORY_PARAMS.map((p) => [p, params.get(p) || undefined])) as HistoryFilter;
  const historyPatch = (f: HistoryFilter) => Object.fromEntries(HISTORY_PARAMS.map((p) => [p, f[p] ?? null])) as Record<string, string | null>;
  const openHistory = (collection?: string, key?: string) => history.open('1', historyPatch({ collection, key }));
  const closeHistory = () => history.set({ history: null, ...historyPatch({}) });
  const guarded = (run: () => void) => () => {
    if (guardStaged(workspace, envSlug, player.id, toasts)) run();
  };

  return (
    <section className="env-page players-page player-page" data-testid="player">
      <PlayerHeader player={player} />
      <div className="page-toolbar player-actions" role="toolbar" aria-label="Player actions">
        <Button icon={<History aria-hidden="true" />} onClick={() => openHistory()}>History</Button>
        <Button icon={<Activity aria-hidden="true" />} onClick={() => events.open('1')}>Events</Button>
        <Button icon={<Download aria-hidden="true" />} onClick={() => setCommand('export')}>Export</Button>
        <Link className="btn" to={routes.orders(org, gameSlug, envSlug, { player: player.id })}>
          <Receipt aria-hidden="true" />
          Orders
        </Link>
        <Link className="btn btn-ghost" to={back}>
          <ArrowLeft aria-hidden="true" />
          Recent players
        </Link>
        <TesterSwitch env={envSlug} player={player} writeMissing={writeMissing} />
        <span className="spacer" />
        {/* the danger actions together at the right end (section 11.2 the action bar) */}
        <Button variant="danger" icon={<RotateCcw aria-hidden="true" />} onClick={guarded(() => setCommand('reset'))} missingPermission={writeMissing}>
          Reset…
        </Button>
        {banActive(player.ban) ? (
          <Button icon={<Unlock aria-hidden="true" />} onClick={() => setCommand('unban')} missingPermission={writeMissing}>
            Unban
          </Button>
        ) : (
          <Button variant="danger" icon={<Ban aria-hidden="true" />} onClick={() => setCommand('ban')} missingPermission={writeMissing}>
            Ban…
          </Button>
        )}
        <Button variant="danger" icon={<Eraser aria-hidden="true" />} onClick={guarded(() => setCommand('erase'))} missingPermission={writeMissing}>
          Erase…
        </Button>
      </div>
      {player.online && (
        <div className="banner banner-warning" role="note" data-testid="online-banner">
          <AlertTriangle className="banner-icon" aria-hidden="true" />
          <div className="banner-body">{ONLINE_BANNER}</div>
        </div>
      )}
      <PlayerCards env={envSlug} player={player} />
      <section className="player-tree-section" aria-label="Profile">
        <div className="section-head">
          <h2>Profile</h2>
          <span className="muted">The player's saves, typed by the environment's released schema.</span>
        </div>
        <PlayerTree env={envSlug} player={player} release={release} canWrite={canWrite} onHistory={(collection, key) => openHistory(collection, key)} />
      </section>
      <HistoryDrawer
        env={envSlug}
        player={player}
        open={history.value === '1'}
        filter={filter}
        onClose={closeHistory}
        onFilter={(f) => history.set(historyPatch(f))}
        writeMissing={writeMissing}
        onRestore={(v) => guarded(() => setRestoreVersion(v))()}
        onRestoreAt={(collection) => guarded(() => setRestoreAt({ collection }))()}
      />
      <EventsDrawer env={envSlug} player={player} open={events.value === '1'} onClose={() => events.set({ events: null })} />
      <CommandDialogsHost
        env={envSlug}
        player={player}
        release={release}
        command={command}
        onCloseCommand={() => setCommand(null)}
        restoreVersion={restoreVersion}
        onCloseRestore={() => setRestoreVersion(null)}
        restoreAt={restoreAt}
        onCloseRestoreAt={() => setRestoreAt(null)}
      />
    </section>
  );
}
